import { Outlet, NavLink, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n/LangContext";
import { LayoutDashboard, Package, KeyRound, ScrollText, LogOut, Globe, Shield, ChevronDown, Users, Link2 } from "lucide-react";
import { Toaster } from "sonner";
import { useState } from "react";
import { SUPPORTED_LANGS } from "@/i18n/translations";

export default function AdminLayout() {
  const { admin, logout, loading } = useAuth();
  const { t, lang, setLang } = useLang();
  const loc = useLocation();
  const [langOpen, setLangOpen] = useState(false);

  if (loading) return <div className="min-h-screen grid place-items-center text-zinc-500">{t("common.loading")}</div>;
  if (!admin && loc.pathname !== "/admin/login") return <Navigate to="/admin/login" replace />;
  if (loc.pathname === "/admin/login") return <Outlet />;

  const links = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: t("admin.dashboard") },
    { to: "/admin/users", icon: Users, label: t("admin.user_mgmt") },
    { to: "/admin/products", icon: Package, label: t("admin.products") },
    { to: "/admin/suppliers", icon: Link2, label: "Suppliers" },
    { to: "/admin/keys", icon: KeyRound, label: t("admin.keys") },
    { to: "/admin/logs", icon: ScrollText, label: t("admin.logs") },
  ];

  return (
    <div className="min-h-screen flex bg-[#050505]" data-testid="admin-shell">
      <aside className="w-60 border-e border-white/5 bg-[#080808] flex flex-col">
        <div className="px-5 py-5 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand to-brand-cyan flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold">KHA GAMES</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Admin Panel</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to} to={l.to} end={l.end}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive ? "bg-brand/10 text-white border border-brand/30" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`admin-nav-${l.to.split("/").pop() || "dashboard"}`}
            >
              <l.icon className="w-4 h-4" />
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/5 space-y-1">
          <div className="relative">
            <button onClick={() => setLangOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-400 hover:text-white hover:bg-white/5" data-testid="admin-lang-toggle">
              <span className="flex items-center gap-3"><Globe className="w-4 h-4" /> {SUPPORTED_LANGS.find(l => l.code === lang)?.name}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {langOpen && (
              <div className="absolute bottom-full mb-2 start-0 right-0 bg-[#0c0c0c] border border-white/10 rounded-lg overflow-hidden">
                {SUPPORTED_LANGS.map((l) => (
                  <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} className={`w-full text-start px-4 py-2 text-sm hover:bg-white/5 ${lang === l.code ? "text-brand-cyan" : "text-zinc-300"}`}>{l.name}</button>
                ))}
              </div>
            )}
          </div>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-red-400 hover:bg-red-500/10" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> {t("admin.logout")}
          </button>
          {admin && <div className="px-3 pt-2 text-[10px] text-zinc-600 truncate">{admin.email}</div>}
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}
